import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Settings, Wrench, X, ArrowLeft, ArrowRight, House, RotateCw, MoreVertical, FolderOpen, ExternalLink, Pin, PinOff, Share } from 'lucide-react'
import { ShellTopBarPortal, AppTopBar, MiniappIcon } from '@papan/core-ui/ui'
import { usePageTitle } from '@papan/brand/react'
import { filesApi, miniappApi } from '@papan/files/client'
import { useAppWrites, openWriteFailures, useIdentitySwitchEpoch } from '@papan/files/client/queries'
import { presentsAsMiniapp, stripPointerSuffix, appDisplayName, parseMiniappIcon, parseMiniappIconRev, type MiniappIconSpec } from '@papan/types'
import { useRealtimeOptional } from '@papan/realtime/client'
import { ShareLinksDialog } from '@papan/share-links/ui'
import { isAppEligibleForFile } from '@papan/share-links/client'
import { usePreviewSession } from '../client/usePreviewSession.js'
import { useFileActions } from './useFileActions.js'
import { HtmlViewer, type HtmlViewerHandle } from './HtmlViewer.js'
import { PdappLoadMorph, usePdappLoadMorph } from './PdappLoadMorph.js'
import { useAppRunNoticeToast } from './useAppRunNoticeToast.js'
import { PreviewConsentBanner } from './PreviewConsentBanner.js'
import { MiniAppInstallGate } from './MiniAppInstallGate.js'
import { MiniAppNoServeView } from './MiniAppNoServeView.js'
import { MiniAppSettingsModifier } from './MiniAppSettingsModifier.js'
import { OpenExternalConfirm } from './OpenExternalConfirm.js'
import { IconButton } from './ToolbarControls.js'
import { ToolbarPopover } from './ToolbarPopover.js'
import type { OpenFileTargetWire, OpenPdappTargetWire, OpenFileOptsWire } from './HtmlViewer.js'
import { frameMessageTarget } from './app-origin-frame.js'
import { isOwnTenantAppRow } from './app-page-link-answer.js'

/**
 * Standalone app host — runs a mini-app (.miniapp folder, Mode B) or a
 * standalone HTML file (Mode A) as a TOP-LEVEL shell window instead of a
 * workspace tab (Phase 2 of docs/plans/2026-07-05_OS_SHELL_REDESIGN.md).
 *
 * CHROMELESS by design: no editor toolbar — apps run the way ppn.gg serves
 * them. The window is an OS-like container: a `.ws-topbar` (app title left,
 * theme toggle + red close right) over a rounded `.ws-card-hero` holding
 * the sandboxed app iframe. The ambient `.ws-stage-app` tint behind it is
 * painted by the shell (App.tsx), like the drive stage.
 *
 * Composition, not reimplementation: `usePreviewSession` owns mint /
 * refresh / consent; `HtmlViewer` owns the sandboxed iframe, SSE reload
 * double-buffering, and the postMessage bridges. This component resolves
 * WHAT to serve and wires navigation → URL sync:
 *   .miniapp folder → its index.html entry, run folder = the folder
 *   .html file     → the file itself, run folder = the session root
 */

interface AppWindowProps {
  /** The hosted file/folder id (the shell instance's `ref`). */
  refId: string
  kind: 'miniapp' | 'html'
  isActive: boolean
  /** In-app document path (relative to the app root) to open at mount —
   *  the `/app/:fileId/*` URL splat. Latched at first render. */
  initialPath?: string
  /** The app's OWN `?query#hash` for that first page (a deep link's, already encoded and
   *  without Papan's own params). Latched at first render, like `initialPath`. */
  initialSuffix?: string
  /** Reports the resolved display name for the dock label. */
  onResolvedTitle?: (title: string) => void
  /** Reports the resolved mini-app icon spec for the dock chip (miniapp only;
   *  null = name-derived default). */
  /** Reports the resolved icon AND its cache-bust rev — the dock chip needs both
   *  to draw an IMAGE icon; the spec alone only yields the gradient default. */
  onResolvedIcon?: (icon: MiniappIconSpec | null, rev: string | null) => void
  /** In-app navigation — the new document's path relative to the app root
   *  ('' = the default entry). The shell replace-syncs the URL splat. */
  onAppNavigate?: (relPath: string) => void
  /** The window's red close button. */
  onClose?: () => void
  /** Open this mini-app in the workspace editor on the Settings face (the wrench).
   *  Threaded from App.tsx (owns router + requestMiniappView). */
  onOpenEditor?: () => void
  onOpenFileTarget?: (target: OpenFileTargetWire, ctx: { appFolderId: string | null; opts: OpenFileOptsWire }) => void
  /** #788 — the running app asked to LAUNCH another PDApp by folder id. */
  onOpenPdappTarget?: (target: OpenPdappTargetWire, ctx: { opts: OpenFileOptsWire }) => void
  // ── ⋯ overflow-menu actions (shell-owned; App.tsx wires them) ──────────
  /** "Open in new tab" — pop the app out to its own browser tab. */
  onOpenNewTab?: () => void
  /** Whether this app is pinned to the dock (drives Pin ↔ Unpin). */
  isPinned?: boolean
  /** Toggle the dock pin. */
  onTogglePin?: () => void
  /** "Reveal in Drive" — App.tsx navigates to the app's containing folder and
   *  highlights it; the window supplies its own parent folder id. */
  onRevealInDrive?: (parentFolderId: string | null) => void
  /** #538 — the PDApps list (favorites + recent) in the current org (the host builds the address). */
  onOpenAppsList?: () => void
  /** Owning tenant — decides org-only actions in the ⋮ menu (#437). */
  tenantId?: string
}

type Resolved =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; entryFileId: string; entryFileName: string; parentId: string | null; fullPath: string | null; title: string }
  // .miniapp with no index.html — runs background work (or nothing yet).
  | { state: 'tasks'; title: string; triggered: number; scheduled: number }
  | { state: 'empty'; title: string }

/** #1316 review round 1 (owner, final): an org-email switch remounts the window, keyed like TabContent — the app was
 *  resolved, its session minted and its iframe loaded as the previous identity, and nothing in it re-asks. (A tenant
 *  switch closes the running apps; an account end reloads the page.) */
export function AppWindow(props: AppWindowProps) {
  const identityEpoch = useIdentitySwitchEpoch()
  return <AppWindowBody key={identityEpoch} {...props} />
}

function AppWindowBody({ refId, kind, isActive, initialPath, initialSuffix, onResolvedTitle, onResolvedIcon, onAppNavigate, onClose, onOpenEditor, onOpenFileTarget, onOpenPdappTarget, onOpenNewTab, isPinned, onTogglePin, onRevealInDrive, onOpenAppsList, tenantId }: AppWindowProps) {
  const [resolved, setResolved] = useState<Resolved>({ state: 'loading' })
  // #538 — the loading morph: when this window started, whether its page has loaded, and whether
  // the overlay is still up (kept mounted a moment after, to fade out).
  const morphStartRef = useRef(performance.now())
  const [appLoaded, setAppLoaded] = useState(false)
  const markAppLoaded = useCallback(() => setAppLoaded(true), [])
  const morphOn = usePdappLoadMorph(morphStartRef.current, appLoaded)
  const [morphMounted, setMorphMounted] = useState(true)
  const [gateOwnUi, setGateOwnUi] = useState(false)
  useEffect(() => {
    if (morphOn) return
    const t = window.setTimeout(() => setMorphMounted(false), 380)
    return () => window.clearTimeout(t)
  }, [morphOn])
  // In-place settings — the gear opens the mini-app's Settings page as an
  // overlay over the running app (the wrench opens it in the full editor).
  const [settingsOpen, setSettingsOpen] = useState(false)
  // An app whose queued database writes failed (undismissed): a dot on the gear, for its editors only (the server
  // answers no one else) — the settings it opens show which trigger and why.
  const writeFailures = openWriteFailures(useAppWrites(kind === 'miniapp' ? refId : null).data).length
  // ⋯ overflow menu (reveal / open-in-new-tab / pin / close) + Share dialog.
  const [menuOpen, setMenuOpen] = useState(false)
  const menuBtnRef = useRef<HTMLButtonElement>(null)
  const [shareOpen, setShareOpen] = useState(false)
  // The app's OWN row (folder containing the .miniapp / the .html file) — for
  // "Reveal in Drive" (its parent) and Share (name/path/isFolder). Distinct from
  // the RUN folder (resolved.parentId), which for a mini-app is the folder itself.
  const [appMeta, setAppMeta] = useState<{ name: string; path: string | null; isFolder: boolean; parentId: string | null; fileType?: string | null } | null>(null)
  // (#327) TRUE when this app is running through a `.shared`/`.chain` MOUNT rather
  // than a native folder. The authoring surfaces are owned by the SOURCE org and
  // have no mount-aware path yet — "Edit app" routes to a workspace on the pointer
  // id (Track B) and "App settings" talks to the native-only miniapp-access routes
  // — so both are hidden here rather than left to dead-end or, worse, wrap the
  // mount in an agent-enabled project the recipient can't actually write.
  const isMountedApp = appMeta?.fileType === 'shared' || appMeta?.fileType === 'chain'
  // Back/forward drive the LIVE iframe; refresh/home remount it (fresh iframe,
  // back at entry) via this key. Nav availability is modeled parent-side off the
  // iframe's papan:nav reports (a cross-origin iframe can't be queried).
  const htmlViewerRef = useRef<HtmlViewerHandle>(null)
  // #184 Phase 4 — the app deck is where an end user actually runs a PDApp, so
  // this is the surface the notice exists for. Server-gated on readwrite.
  const onAppRunNotice = useAppRunNoticeToast()
  const [navState, setNavState] = useState({ canBack: false, canForward: false })
  const navIndexRef = useRef(0)
  const navLenRef = useRef(1)
  const navFromButtonRef = useRef(false)
  // Pending external-link open — the sandboxed app relays `papan:openExternal`
  // (a `<a target="_blank">` click / window.open) and we gate it behind the
  // same confirm modal the editor uses, so a standalone window isn't a silent
  // exfil hole. Without this, external links from an app window did nothing.
  const [openExternalUrl, setOpenExternalUrl] = useState<string | null>(null)
  // Mini-app icon spec (from the folder row's metadata_json) — drives the
  // top-bar chip; null renders the name-derived default.
  const [iconSpec, setIconSpec] = useState<MiniappIconSpec | null>(null)
  // Pairs with `refId` so an IMAGE icon can load its server-composited PNG; the
  // spec alone only gets you the name-derived gradient.
  const [iconRev, setIconRev] = useState<string | null>(null)
  // Latched: a later splat change must NOT re-enter the iframe (the app
  // navigates itself; the splat follows via onAppNavigate → replace-sync).
  const initialPathRef = useRef(initialPath)
  const initialSuffixRef = useRef(initialSuffix)

  useEffect(() => {
    let cancelled = false
    const fail = (message: string) => { if (!cancelled) setResolved({ state: 'error', message }) }
    setResolved({ state: 'loading' })

    filesApi.getFileInfo(refId)
      .then(async info => {
        if (cancelled) return
        // The app's own row — for Reveal in Drive (its parent) + Share.
        setAppMeta({ name: info.name, path: (info as { path?: string }).path ?? null, isFolder: info.isFolder, parentId: info.parentId ?? null, fileType: info.fileType })
        if (kind === 'miniapp') {
          // (#327) Also accepts a folder-target `.shared` mount whose SOURCE is a
          // `.miniapp` — `listChildren` synthesizes its children as compound mount
          // ids and the session mint resolves them to the source app folder, so the
          // app boots as the source principal with this user as the actor. The kind
          // gate inside `presentsAsMiniapp` keeps a hand-made `X.miniapp.shared`
          // folder from posing as an app.
          // `presentsAsMiniapp` owns the folder test now: a real package must be a
          // folder, but a `.shared` pointer row is a FILE row that presents as one,
          // so an outer `!info.isFolder` check here would reject every mount.
          if (!presentsAsMiniapp(info.name, info.fileType, info.isFolder)) return fail('This item is not a PDApp.')
          const title = appDisplayName(stripPointerSuffix(info.name))
          onResolvedTitle?.(title)
          const metadataJson = (info as { metadataJson?: string | null }).metadataJson
          const icon = parseMiniappIcon(metadataJson)
          setIconSpec(icon)
          setIconRev(parseMiniappIconRev(metadataJson))
          onResolvedIcon?.(icon, parseMiniappIconRev(metadataJson))
          // Entry point: index.html at the app folder's root.
          const listing = await filesApi.listChildren(refId, { limit: 200 })
          const { files } = listing
          if (cancelled) return
          // (#327) A mount the kernel won't resolve for this caller (revoked grant,
          // hidden placement, deleted source) lists EMPTY with `mount.state:'broken'`.
          // Without this the empty result falls through to the "no content to run"
          // state below and blames the app's author for what is actually a denial.
          if (listing.mount?.state === 'broken' || listing.parentPermission === 'hidden' || listing.parentPermission === 'none') {
            return fail('You don’t have access to this app. Ask the owner to share it with you.')
          }
          const entry = files.find(f => !f.isFolder && f.name.toLowerCase() === 'index.html')
          if (!entry) {
            // An app the caller cannot READ lists EMPTY, and the `parentPermission`
            // check above does not always catch it: the listing REPORTS `readcopy`
            // for a hidden folder whenever the caller holds an explicit grant that
            // the ancestor whitelist covers, so a member with a grant elsewhere gets
            // "nothing to run yet" for an app they simply may not open. That blames
            // the author for a denial. #788 makes this common — an author's launch
            // button reaches viewers who cannot open the target, by design — so ask
            // for the caller's own permission before concluding the app is empty.
            try {
              const perm = await filesApi.getPermission(refId)
              if (cancelled) return
              if (perm === 'hidden' || perm === 'none') {
                return fail('You don’t have access to this app. Ask the owner to share it with you.')
              }
            } catch (e) {
              // `GET /permission` answers 403 for `none` — the real denial arrives
              // HERE, not as a value, so the status has to be read or this catch
              // reports "check your connection" for the commonest case there is.
              if (cancelled) return
              const status = (e as { status?: number }).status
              return fail(status === 403 || status === 404
                ? 'You don’t have access to this app. Ask the owner to share it with you.'
                : 'Couldn’t check your access to this app. Check your connection and try again.')
            }
            // No page to serve — the app may still run background tasks.
            let triggered = 0, scheduled = 0
            try {
              const { triggers } = await miniappApi.getTriggers(refId)
              for (const t of triggers) {
                if ((t?.when as { source?: string } | undefined)?.source === 'schedule') scheduled++
                else triggered++
              }
            } catch { /* none readable */ }
            if (cancelled) return
            return setResolved(triggered + scheduled > 0 ? { state: 'tasks', title, triggered, scheduled } : { state: 'empty', title })
          }
          setResolved({ state: 'ready', entryFileId: entry.id, entryFileName: entry.name, parentId: refId, fullPath: null, title })
        } else {
          if (info.isFolder || !/\.html?$/i.test(info.name)) return fail('This item is not an HTML page.')
          let title = appDisplayName(info.name)
          if (title.toLowerCase() === 'index') {
            // "index" says nothing — title the app by its containing folder.
            const segs = (info.path ?? '').split('/').filter(Boolean)
            if (segs.length >= 2) title = segs[segs.length - 2]
          }
          onResolvedTitle?.(title)
          setResolved({ state: 'ready', entryFileId: refId, entryFileName: info.name, parentId: info.parentId ?? null, fullPath: info.path ?? null, title })
        }
      })
      .catch(() => fail('App not found — it may have been deleted or you may not have access.'))
    return () => { cancelled = true }
    // onResolvedTitle intentionally omitted: identity changes per render, and
    // re-resolving on it would loop. refId/kind fully determine the result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId, kind])

  // Live-update the top-bar chip when THIS mini-app's icon is edited (from the
  // editor, the agent tool, or another tab) — the initial spec is latched at
  // load above, so without this the running app's header stays stale until it's
  // reopened. Mirrors the taskbar dock chip (App.tsx → setAppIcon) and the
  // breadcrumb crumb, all fed by the same `file:updated` event.
  const realtime = useRealtimeOptional()
  const onResolvedIconRef = useRef(onResolvedIcon)
  onResolvedIconRef.current = onResolvedIcon
  useEffect(() => {
    if (kind !== 'miniapp' || !realtime) return
    return realtime.onFileEvent((event) => {
      if (event.type !== 'file:updated' || event.fileId !== refId || !event.file) return
      if (!presentsAsMiniapp(event.file.name, event.file.fileType, event.file.isFolder)) return
      const md = event.file.metadataJson ?? null
      const icon = parseMiniappIcon(md)
      const nextRev = parseMiniappIconRev(md)
      setIconSpec(icon)
      setIconRev(nextRev)
      onResolvedIconRef.current?.(icon, nextRev)
    })
  }, [refId, kind, realtime])

  const ready = resolved.state === 'ready'
  const {
    baseUrl,
    appOrigin,
    previewOrigin,
    currentRoot,
    pendingConsent,
    approveConsent,
    error: sessionError,
  } = usePreviewSession({
    fileId: ready ? resolved.entryFileId : '',
    mode: 'preview',
    enabled: ready,
    // #1408: run on the app's own preview origin where the host offers one (camera, microphone).
    appOrigin: true,
  })

  // The app's DEFAULT entry, relative to the session root (mirrors
  // HtmlEditor's entryPathRelToRoot, minus the share-session cases this
  // surface never hits).
  const defaultEntryRel = useMemo(() => {
    if (!ready) return null
    if (currentRoot?.entryPath) return currentRoot.entryPath
    if (pendingConsent?.entryPath) return pendingConsent.entryPath
    if (resolved.fullPath && currentRoot?.rootPath) {
      const root = currentRoot.rootPath.replace(/\/+$/, '')
      if (resolved.fullPath.startsWith(root + '/')) return resolved.fullPath.slice(root.length + 1)
    }
    return resolved.entryFileName
  }, [ready, resolved, currentRoot?.entryPath, currentRoot?.rootPath, pendingConsent?.entryPath])

  const entryRel = initialPathRef.current || defaultEntryRel || undefined
  // `pdapp.links.page()` — the host builds Papan's own address of the app from the ref
  // this window's `/app/<ref>` route opens it by.
  // Only the shell tenant's own content gets that address (an app through a `.shared` /
  // `.chain` would learn the viewer's tenant) — unknown until the row is resolved.
  const ownContent = appMeta ? isOwnTenantAppRow(refId, appMeta.fileType) : false
  const appPage = useMemo(() => ({ ref: refId, kind, tenantId: tenantId ?? null, ownContent }), [refId, kind, tenantId, ownContent])

  // The app's actual RUN folder (drives the Papan-bar embed + consent
  // relay): the folder the app is SERVED from, not the file's raw parent.
  const appRunFolderId = currentRoot?.rootId ?? (ready ? resolved.parentId : null)

  // Debug-log target for the app-run surface (mirrors HtmlEditor). Without
  // this the injected console-capture shim's `papan:console` batches are
  // dropped by HtmlViewer — so console output/errors generated while the app
  // is RUN (not just edited) never reach the log. Mode B → the `.miniapp`
  // folder; Mode A → the loose HTML file. The debug-log route re-checks
  // write/edit permission, so it's safe to target here unconditionally.
  const debugLogTarget = useMemo<{ kind: 'miniapp' | 'html'; id: string } | null>(() => {
    if (resolved.state !== 'ready') return null
    if (kind === 'miniapp') return appRunFolderId ? { kind: 'miniapp', id: appRunFolderId } : null
    return { kind: 'html', id: resolved.entryFileId }
  }, [resolved, kind, appRunFolderId])

  // In-iframe navigation → report the new document's root-relative path so
  // the shell can sync the URL splat. The session URL shape is
  // `<origin>/s/<token>/<relPath>`; the default entry reports as '' (base).
  const onAppNavigateRef = useRef(onAppNavigate)
  onAppNavigateRef.current = onAppNavigate
  const defaultEntryRelRef = useRef(defaultEntryRel)
  defaultEntryRelRef.current = defaultEntryRel
  const handleNavigate = useMemo(() => (url: string) => {
    // Cross-origin nav lands outside our preview origin (HtmlViewer's probe
    // sends the `about:external` sentinel) — it's off our history.
    if (url === 'about:external') return
    let rel = ''
    try { const m = new URL(url).pathname.match(/^\/s\/[^/]+\/(.*)$/); if (m) rel = decodeURIComponent(m[1]) } catch { /* not a URL */ }
    onAppNavigateRef.current?.(rel === defaultEntryRelRef.current ? '' : rel)
    // Model back/forward availability off the reports (same as HtmlEditor):
    // a button-driven nav already moved the index; a real load pushes / resets.
    if (navFromButtonRef.current) { navFromButtonRef.current = false; return }
    if (!rel || rel === defaultEntryRelRef.current) { navIndexRef.current = 0; navLenRef.current = 1 }
    else { navIndexRef.current += 1; navLenRef.current = navIndexRef.current + 1 }
    setNavState({ canBack: navIndexRef.current > 0, canForward: navIndexRef.current < navLenRef.current - 1 })
  }, [])

  // Refresh / Home — SOFT reload (HtmlViewer's double-buffered swap, no remount
  // / session re-mint / flash): reloads the entry page. Reset the tracked nav
  // position so back/forward disable at the root (the entry load re-reports it).
  const reloadApp = () => {
    navIndexRef.current = 0; navLenRef.current = 1; navFromButtonRef.current = false
    setNavState({ canBack: false, canForward: false })
    htmlViewerRef.current?.reload()
  }
  const goBack = () => {
    if (navIndexRef.current <= 0) return
    navFromButtonRef.current = true
    htmlViewerRef.current?.iframe?.contentWindow?.postMessage({ type: 'papan:nav:back' }, frameMessageTarget(htmlViewerRef.current?.iframe))
    navIndexRef.current -= 1
    setNavState({ canBack: navIndexRef.current > 0, canForward: navIndexRef.current < navLenRef.current - 1 })
  }
  const goForward = () => {
    if (navIndexRef.current >= navLenRef.current - 1) return
    navFromButtonRef.current = true
    htmlViewerRef.current?.iframe?.contentWindow?.postMessage({ type: 'papan:nav:forward' }, frameMessageTarget(htmlViewerRef.current?.iframe))
    navIndexRef.current += 1
    setNavState({ canBack: navIndexRef.current > 0, canForward: navIndexRef.current < navLenRef.current - 1 })
  }

  const title = (resolved.state === 'ready' || resolved.state === 'tasks' || resolved.state === 'empty') ? resolved.title : '…'

  // The running app names the tab. `'…'` is the placeholder shown while the
  // manifest resolves — passing it would flash "… · Papan" in the tab strip,
  // so the tab keeps whatever it had until the real name lands. Gated on
  // `isActive` because the shell keeps every running app MOUNTED behind the
  // one on screen.
  usePageTitle(title === '…' ? null : title, isActive)

  // #538 — a PDApp's window shows the splash's PDF → PDApp morph while it starts and loads (a
  // standalone HTML page keeps the plain line). One start time across the two bodies below.
  // (The morph itself is drawn ONCE, over the window — see `morphOverlay` — so swapping these bodies never restarts it.)
  const startingBody = kind === 'miniapp'
    ? <div className="flex-1" />
    : <div className="flex-1 flex items-center justify-center themed-text-muted text-sm">Starting app…</div>
  let body: React.ReactNode
  if (resolved.state === 'loading') {
    body = startingBody
  } else if (resolved.state === 'error') {
    body = (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-lg themed-text">Can’t run this app</p>
          <p className="text-sm themed-text-muted">{resolved.message}</p>
        </div>
      </div>
    )
  } else if (resolved.state === 'tasks') {
    body = <MiniAppNoServeView triggered={resolved.triggered} scheduled={resolved.scheduled} />
  } else if (resolved.state === 'empty') {
    body = <MiniAppNoServeView triggered={0} scheduled={0} />
  } else if (pendingConsent) {
    body = (
      <PreviewConsentBanner
        proposedRootPath={pendingConsent.rootPath}
        proposedRootId={pendingConsent.rootId}
        browseRootId={pendingConsent.browseRootId}
        fileId={resolved.entryFileId}
        previewOrigin={previewOrigin}
        entryFilePath={defaultEntryRel ?? undefined}
        onAllow={approveConsent}
      />
    )
  } else if (sessionError) {
    body = (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-lg themed-text">Can’t run this app</p>
          <p className="text-sm themed-text-muted">{sessionError}</p>
        </div>
      </div>
    )
  } else if (baseUrl && entryRel) {
    body = (
      <div className="relative flex-1 flex flex-col min-h-0">
      <HtmlViewer
        onFirstLoad={markAppLoaded}
        ref={htmlViewerRef}
        content=""
        baseUrl={baseUrl}
        entryFile={entryRel}
        entrySuffix={initialSuffixRef.current}
        appPage={appPage}
        previewFileId={resolved.entryFileId}
        parentId={appRunFolderId}
        debugLogTarget={debugLogTarget}
        onNavigate={handleNavigate}
        onOpenFileTarget={onOpenFileTarget}
        onOpenPdappTarget={onOpenPdappTarget}
        onRequestOpenExternal={(url) => setOpenExternalUrl(url)}
        onAppRunNotice={onAppRunNotice}
        accountsContext="app"
        screenActive={isActive}
        barActions={{ onShare: appMeta ? () => setShareOpen(true) : undefined, onLocate: onRevealInDrive && appMeta ? () => onRevealInDrive(appMeta.parentId ?? null) : undefined, onOpenList: onOpenAppsList, isPdapp: kind === 'miniapp', isPinned, onTogglePin, favoriteAppId: kind === 'miniapp' && !isMountedApp ? refId : undefined, icon: kind === 'miniapp' ? { spec: iconSpec, rev: iconRev, refId } : undefined }}
        appOrigin={appOrigin}
      />
      </div>
    )
  } else {
    body = startingBody
  }

  // Mode B (mini-app) gates the WHOLE run on install + consent — the same
  // stepped flow as the in-tab runner. This must wrap the entire body (not just
  // the served page) so a no-serve app's background-task screen can't bypass
  // consent. Mode A (standalone HTML) has no install concept → run as-is.
  const gated = kind === 'miniapp'
    ? <MiniAppInstallGate folderId={refId} folderName={title !== '…' ? title : undefined} loadingView={startingBody} onOwnUiChange={setGateOwnUi}>{body}</MiniAppInstallGate>
    : body

  // (#437) The ⋮ menu's access/sharing actions — one catalog shared with the
  // workspace tab menu (useFileActions). Additive: the standalone Share icon
  // and the wrench (Edit app) below stay exactly as they are.
  const fileActions = useFileActions({
    fileId: refId,
    name: appMeta?.name ?? title,
    path: appMeta?.path ?? null,
    isFolder: !!appMeta?.isFolder,
    tenantId,
    fileType: appMeta?.fileType ?? null,
    surface: 'app-shell',
    // Mode B only, and never for a mounted app — a `.shared`/`.chain` mount has
    // no authoring path (#327). Mirrors the existing wrench gate.
    onEditApp: kind === 'miniapp' && !isMountedApp ? onOpenEditor : undefined,
  })

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
      {/* Window title bar lives in the shell's ONE permanent top bar — the
          active app portals it there (app title left, theme toggle + red
          close right). */}
      <ShellTopBarPortal active={isActive}>
        <AppTopBar
          area="workspace"
          left={
            <div className="flex items-center gap-0.5 min-w-0">
              {/* App identity — icon (mini-app) + name. */}
              <span className="flex items-center gap-1.5 min-w-0">
                {kind === 'miniapp' && <MiniappIcon spec={iconSpec} name={title} size={18} refId={refId} rev={iconRev} />}
                <span className="text-xs font-medium themed-text truncate max-w-[240px] select-none cursor-default">{title}</span>
              </span>
              {/* ⋮ overflow menu (right after the file name) — reveal / open-tab / pin / close. */}
              <button
                ref={menuBtnRef}
                onClick={() => setMenuOpen(o => !o)}
                data-active={menuOpen || undefined}
                className="ws-card-iconbtn ml-0.5"
                data-tooltip="More"
                aria-label="More"
              >
                <MoreVertical size={14} />
              </button>
              <ToolbarPopover className="ws-menu min-w-[188px]" anchorRef={menuBtnRef} open={menuOpen} onClose={() => setMenuOpen(false)}>
                <button type="button" className="ws-menu-item cursor-pointer" onClick={() => { setMenuOpen(false); onTogglePin?.() }}>
                  {isPinned ? <PinOff size={14} className="themed-text-muted shrink-0" /> : <Pin size={14} className="themed-text-muted shrink-0" />}
                  <span>{isPinned ? 'Unpin' : 'Pin'}</span>
                </button>
                <button type="button" className="ws-menu-item cursor-pointer" onClick={() => { setMenuOpen(false); onOpenNewTab?.() }}>
                  <ExternalLink size={14} className="themed-text-muted shrink-0" /><span>Open in new tab</span>
                </button>
                <button type="button" className="ws-menu-item cursor-pointer" onClick={() => { setMenuOpen(false); onRevealInDrive?.(appMeta?.parentId ?? null) }}>
                  <FolderOpen size={14} className="themed-text-muted shrink-0" /><span>Reveal in Drive</span>
                </button>
                {/* Gate on appMeta: until it resolves the target name/path fall
                    back to the display title, so a dialog opened in that window
                    would show the wrong name. It loads sub-second. The catalog
                    returns the Share / Develop sections (headers + separators). */}
                {appMeta && fileActions.items.length > 0 && <div className="ws-menu-sep" />}
                {appMeta && fileActions.items.map((item, idx) => {
                  if (item.kind === 'separator') return <div key={idx} className="ws-menu-sep" />
                  if (item.kind === 'header') return (
                    <div key={idx} className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider themed-text-muted select-none">
                      {item.label}
                    </div>
                  )
                  if (item.kind !== 'item') return null
                  return (
                    <button
                      key={idx}
                      type="button"
                      className={`ws-menu-item${item.disabled ? ' opacity-40 cursor-not-allowed' : ' cursor-pointer'}`}
                      disabled={item.disabled}
                      onClick={() => { if (item.disabled) return; setMenuOpen(false); item.onClick?.() }}
                    >
                      {item.icon && <item.icon size={14} className="themed-text-muted shrink-0" />}
                      <span>{item.label}</span>
                    </button>
                  )
                })}
                <div className="ws-menu-sep" />
                <button type="button" className="ws-menu-item cursor-pointer" onClick={() => { setMenuOpen(false); onClose?.() }}>
                  <X size={14} className="themed-text-muted shrink-0" /><span>Close</span>
                </button>
              </ToolbarPopover>
              {/* Nav controls — to the RIGHT of the title + ⋮. HTML: back ·
                  forward · home; mini-app: refresh. */}
              <span className="ml-1 flex items-center gap-0.5">
                {kind === 'html' ? (
                  <>
                    <IconButton icon={<ArrowLeft size={14} />} label="Back" disabled={!navState.canBack} onClick={goBack} />
                    <IconButton icon={<ArrowRight size={14} />} label="Forward" disabled={!navState.canForward} onClick={goForward} />
                    <IconButton icon={<House size={14} />} label="Home" onClick={reloadApp} />
                  </>
                ) : (
                  <IconButton icon={<RotateCw size={14} />} label="Refresh" onClick={reloadApp} />
                )}
              </span>
            </div>
          }
          right={
            <>
              {kind === 'miniapp' && !isMountedApp && (
                <>
                  <button
                    onClick={() => setSettingsOpen(o => !o)}
                    data-active={settingsOpen || undefined}
                    className="ws-card-iconbtn relative"
                    data-tooltip={writeFailures ? "App settings — some data wasn't saved" : 'App settings'}
                    aria-label={writeFailures ? "App settings (some data wasn't saved)" : 'App settings'}
                  >
                    <Settings size={14} />
                    {writeFailures > 0 && <span aria-hidden data-testid="app-settings-write-failure" className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 pointer-events-none" />}
                  </button>
                  {onOpenEditor && (
                    <button
                      onClick={onOpenEditor}
                      className="ws-card-iconbtn"
                      data-tooltip="Edit app"
                      aria-label="Edit app"
                    >
                      <Wrench size={14} />
                    </button>
                  )}
                </>
              )}
              {/* Share — seated just before the theme toggle. */}
              <button
                onClick={() => setShareOpen(true)}
                className="ws-card-iconbtn"
                data-tooltip="Share"
                aria-label="Share"
              >
                <Share size={14} />
              </button>
            </>
          }
          onClose={onClose}
          closeTitle="Close app"
        />
      </ShellTopBarPortal>
      {/* Rounded app container floating on the app stage. */}
      <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5">
        <div className="ws-card-hero hero-app flex-1 flex flex-col min-w-0 min-h-0 relative">
          {gated}
          {/* #538 — over the window until the PDApp has loaded; never over the install steps, an error
              or a PDApp with no page (its tasks / empty screens say what they are). */}
          {kind === 'miniapp' && morphMounted && !gateOwnUi && !sessionError
            && resolved.state !== 'error' && resolved.state !== 'tasks' && resolved.state !== 'empty'
            && <PdappLoadMorph startedAt={morphStartRef.current} leaving={!morphOn} />}
          {/* In-place settings overlay (mini-apps only). */}
          {kind === 'miniapp' && settingsOpen && !isMountedApp && (
            <div className="absolute inset-0 z-10 flex flex-col themed-bg-primary">
              <div className="h-10 shrink-0 border-b themed-border flex items-center justify-between px-3">
                <span className="text-xs font-medium themed-text flex items-center gap-1.5">
                  <Settings size={13} /> {title !== '…' ? title : 'App'} · Settings
                </span>
                <button
                  onClick={() => setSettingsOpen(false)}
                  className="ws-card-iconbtn"
                  data-tooltip="Close settings"
                  aria-label="Close settings"
                >
                  <X size={14} />
                </button>
              </div>
              {/* MUST stay a flex column: MiniAppSettingsModifier's root scrolls
                  via `flex-1 overflow-y-auto`, which is inert in a block parent —
                  it grows to content height and this `overflow-hidden` then clips
                  it with no scrollbar anywhere. Same shape as MiniAppEditor's
                  settings face. */}
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                <MiniAppSettingsModifier folderId={refId} folderName={title !== '…' ? title : undefined} />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* External-link confirm — gates `papan:openExternal` from the app iframe
          (same modal the HTML editor uses). Rendered via a portal to body. */}
      {openExternalUrl && (
        <OpenExternalConfirm
          url={openExternalUrl}
          onConfirm={() => {
            try { window.open(openExternalUrl, '_blank', 'noopener,noreferrer') } catch { /* blocked */ }
            setOpenExternalUrl(null)
          }}
          onCancel={() => setOpenExternalUrl(null)}
        />
      )}
      {/* Share — App-mode link is eligible for a `.miniapp` folder AND a loose
          `.html` file (isAppEligibleForFile). Self-contained, like the mobile
          app-control panel's Share. */}
      {shareOpen && appMeta && (
        <ShareLinksDialog
          fileId={refId}
          fileName={appMeta.name}
          filePath={appMeta.path ?? `/papan/${appMeta.name}`}
          isFolder={appMeta.isFolder}
          isAppEligible={isAppEligibleForFile({ name: appMeta.name, isFolder: appMeta.isFolder })}
          onClose={() => setShareOpen(false)}
        />
      )}
      {appMeta && fileActions.dialogs}
    </div>
  )
}
